import React, { useState } from 'react';
import { Button, Container, Form, Grid, Header, Icon, Message, Segment, Select, Transition } from 'semantic-ui-react';

// Data
const paceTableData = {
  bachelor: { 15: 0, 30: 5, 45: 16, 60: 27, 75: 42, 90: 50, 105: 63, 120: 72, 135: 84, 150: 95, 165: 110, 180: 120 },
  associate: {
    12: 0, 18: 2, 24: 4, 30: 6, 36: 13, 42: 19, 48: 23, 54: 27,
    60: 33, 66: 39, 72: 44, 78: 49, 84: 55, 90: 61,
  },
};

const tapTablesData = {
  'non-remedial': {
    associate: { 1: [0, 0], 2: [6, 1.3], 3: [15, 1.5], 4: [27, 1.8], 5: [39, 2.0], 6: [51, 2.0] },
    bachelor: {
      1: [0, 0], 2: [6, 1.5], 3: [15, 1.8], 4: [27, 1.8], 5: [39, 2.0],
      6: [51, 2.0], 7: [66, 2.0], 8: [81, 2.0], 9: [96, 2.0], 10: [111, 2.0],
    },
  },
  'remedial': {
    associate: { 1: [0, 0], 2: [3, 0.5], 3: [9, 0.75], 4: [18, 1.3], 5: [30, 2.0], 6: [45, 2.0] },
    bachelor: {
      1: [0, 0], 2: [3, 1.1], 3: [9, 1.2], 4: [21, 1.3], 5: [33, 2.0],
      6: [45, 2.0], 7: [60, 2.0], 8: [75, 2.0], 9: [90, 2.0], 10: [105, 2.0],
    },
  },
};

const programOptions = [
  { key: 'bachelor', value: 'bachelor', text: 'Bachelor' },
  { key: 'associate', value: 'associate', text: 'Associate' },
];

const tapTrackOptions = [
  { key: 'non-remedial', value: 'non-remedial', text: 'Non-remedial (2010+)' },
  { key: 'remedial', value: 'remedial', text: 'Remedial/SEEK/CD (2007+)' },
];


const SAPCalculator = () => {
  // Federal State
  const [fedProgram, setFedProgram] = useState('associate');
  const [fedTerms, setFedTerms] = useState('');
  const [fedAttempted, setFedAttempted] = useState('');
  const [fedEarned, setFedEarned] = useState('');
  const [fedGpa, setFedGpa] = useState('');

  // TAP State
  const [showTapSection, setShowTapSection] = useState(false);
  const [tapTrack, setTapTrack] = useState('non-remedial');
  const [tapPayment, setTapPayment] = useState('');
  // Removed tapProgram, tapEarned, tapGpa state as they'll use federal values

  // Results State
  const [federalResults, setFederalResults] = useState([]);
  const [tapResults, setTapResults] = useState([]);
  const [showResultsAnimation, setShowResultsAnimation] = useState(false);

  // View State
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleToggleTAP = () => setShowTapSection(!showTapSection);

  const ResultItem = ({ label, status, message }) => (
    <Message positive={status === 'Pass'} negative={status === 'Fail'} info={status === 'Info'}
             style={{ textAlign: 'left', marginBottom: '0.5em' }}>
      <strong>
        {status === 'Pass' && <Icon name='check circle outline' />}
        {status === 'Fail' && <Icon name='close circle outline' />}
        {status === 'Info' && <Icon name='info circle' />}
        {label}: {status}
      </strong>
      {message && <p>{message}</p>}
    </Message>
  );

  const handleCalculateSAP = () => {
    const terms = parseInt(fedTerms, 10) || 0;
    const attempted = parseFloat(fedAttempted) || 0;
    const earned = parseFloat(fedEarned) || 0; // Used for both Fed and TAP
    const gpa = parseFloat(fedGpa) || 0;       // Used for both Fed and TAP

    let fedOut = [];
    const minGPA = (terms >= 4)
      ? 2.0
      : (attempted > 24 ? 2.0 : (attempted > 12 ? 1.75 : 1.5));
    fedOut.push({
      label: 'GPA',
      status: gpa >= minGPA ? 'Pass' : 'Fail',
      message: gpa < minGPA ? `Minimum required: ${minGPA.toFixed(2)}` : undefined,
    });

    const paceRules = paceTableData[fedProgram];
    let paceStatus = 'Unknown';
    let paceNeeded = 0;
    let paceFoundRule = false;
    const sortedPaceKeys = paceRules ? Object.keys(paceRules).map(Number).sort((a, b) => a - b) : [];

    if (sortedPaceKeys.length > 0) {
      for (const k of sortedPaceKeys) {
        if (attempted <= k) {
          paceNeeded = paceRules[k];
          paceStatus = earned >= paceNeeded ? 'Pass' : 'Fail';
          paceFoundRule = true;
          break;
        }
      }
      if (!paceFoundRule) {
        const lastKey = sortedPaceKeys[sortedPaceKeys.length - 1];
        paceNeeded = paceRules[lastKey];
        paceStatus = earned >= paceNeeded ? 'Pass' : 'Fail';
      }
    } else {
      paceStatus = 'Info';
    }

    if (paceStatus === 'Pass') {
      fedOut.push({ label: 'Pace of Progression', status: 'Pass' });
    } else if (paceStatus === 'Fail') {
      fedOut.push({
        label: 'Pace of Progression',
        status: 'Fail',
        message: `Need ${paceNeeded} earned credits for ${attempted} attempted.`,
      });
    } else if (paceStatus === 'Info') {
      fedOut.push({
        label: 'Pace of Progression',
        status: 'Info',
        message: 'No pace rules defined or applicable for this program.',
      });
    }

    const degreeReq = (fedProgram === 'associate') ? 60 : 120;
    const maxCredits = degreeReq * 1.5;
    fedOut.push({
      label: 'Maximum Timeframe',
      status: attempted <= maxCredits ? 'Pass' : 'Fail',
      message: attempted > maxCredits ? `Max attempted credits: ${maxCredits}` : undefined,
    });
    setFederalResults(fedOut);

    // --- TAP Calculation ---
    let tapOut = [];
    if (showTapSection) {
      // fedEarned (as 'earned') and fedGpa (as 'gpa') are already parsed and available
      // fedProgram is also available
      if (tapPayment) {
        const paymentNum = parseInt(tapPayment, 10);
        if (!isNaN(paymentNum) && paymentNum >= 1 && paymentNum <= 10) {
          const ruleSet = tapTablesData[tapTrack]?.[fedProgram]; // Use fedProgram
          const rule = ruleSet?.[paymentNum];

          if (rule) {
            const [requiredCredits, requiredGpa] = rule;
            if (earned >= requiredCredits) { // Use 'earned' from federal
              tapOut.push({ label: 'TAP Credits', status: 'Pass', message: `Required: ${requiredCredits}` });
            } else {
              tapOut.push({ label: 'TAP Credits', status: 'Fail', message: `Required: ${requiredCredits}` });
            }

            if (gpa >= requiredGpa) { // Use 'gpa' from federal
              tapOut.push({ label: 'TAP GPA', status: 'Pass', message: `Required: ${requiredGpa.toFixed(2)}` });
            } else {
              tapOut.push({ label: 'TAP GPA', status: 'Fail', message: `Required: ${requiredGpa.toFixed(2)}` });
            }
          } else {
            tapOut.push({
              label: 'TAP Rule',
              status: 'Info',
              message: `No TAP rule found for payment #${paymentNum} under ${tapTrack} / ${fedProgram}.`,
            });
          }
        } else {
          tapOut.push({ label: 'TAP Payment #', status: 'Info', message: 'Invalid TAP payment number (1-10).' });
        }
      } else {
        tapOut.push({ label: 'TAP Evaluation', status: 'Info', message: 'Enter TAP payment number to evaluate.' });
      }
    }
    setTapResults(tapOut);

    setShowResultsAnimation(true);
    setIsFormSubmitted(true);
  };

  const handleReset = () => {
    setIsFormSubmitted(false);
    setShowResultsAnimation(false);
    setFedProgram('associate');
    setFedTerms('');
    setFedAttempted('');
    setFedEarned('');
    setFedGpa('');
    setShowTapSection(false);
    setTapTrack('non-remedial');
    setTapPayment('');
    setFederalResults([]);
    setTapResults([]);
  };

  const isFedFormComplete = fedProgram && fedTerms && fedAttempted && fedEarned && fedGpa;
  const isTapSectionReady = !showTapSection || (showTapSection && tapPayment);
  const canCalculate = isFedFormComplete && isTapSectionReady;

  return (
    <Container style={{ marginTop: '2em', marginBottom: '2em' }}>

      {!isFormSubmitted ? (
        <>
          <Segment raised>
            <Header as='h2'>SAP Eligibility Calculator</Header>
            <Form>
              <Form.Group widths='equal'>
                <Form.Field
                  control={Select}
                  label='Program Type'
                  options={programOptions}
                  value={fedProgram}
                  onChange={(e, { value }) => setFedProgram(value)}
                />
                <Form.Input
                  fluid
                  label='Terms Enrolled'
                  type='number'
                  min='0'
                  value={fedTerms}
                  onChange={(e) => setFedTerms(e.target.value)}
                  placeholder='e.g., 3'
                  required
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  fluid
                  label='Attempted Credits'
                  type='number'
                  step='0.1'
                  min='0'
                  value={fedAttempted}
                  onChange={(e) => setFedAttempted(e.target.value)}
                  placeholder='e.g., 45.5'
                  required
                />
                <Form.Input
                  fluid
                  label='Earned Credits'
                  type='number'
                  step='0.1'
                  min='0'
                  value={fedEarned}
                  onChange={(e) => setFedEarned(e.target.value)}
                  placeholder='e.g., 30'
                  required
                />
                <Form.Input
                  fluid
                  label='Current GPA'
                  type='number'
                  step='0.01'
                  min='0'
                  max='4'
                  value={fedGpa}
                  onChange={(e) => setFedGpa(e.target.value)}
                  placeholder='e.g., 2.75'
                  required
                />
              </Form.Group>
            </Form>
          </Segment>

          <Button
            onClick={handleToggleTAP}
            basic
            fluid
            style={{ margin: '1em 0' }}
            icon
            labelPosition='left'
            aria-expanded={showTapSection}
            aria-controls='tap_section_content'
          >
            <Icon name={showTapSection ? 'minus square outline' : 'plus square outline'} />
            {showTapSection ? 'Hide' : 'Show'} NYS TAP SAP Section (Optional)
          </Button>

          <Transition visible={showTapSection} animation='slide down' duration={300}>
            <div id='tap_section_content' style={{ display: showTapSection ? 'block' : 'none' }}>
              <Segment raised style={{ marginTop: 0 }}>
                <Header as='h3'>NYS TAP SAP Inputs</Header>
                <Form>
                  <Form.Group widths='equal'>
                    <Form.Field
                      control={Select}
                      label='Student Type'
                      options={tapTrackOptions}
                      value={tapTrack}
                      onChange={(e, { value }) => setTapTrack(value)}
                    />
                    <Form.Input
                      fluid
                      label='Number of TAP Payments Used'
                      type='number'
                      min='1'
                      max='10' // Max for bachelor, associate might be less
                      value={tapPayment}
                      onChange={(e) => setTapPayment(e.target.value)}
                      placeholder='1-10'
                      required={showTapSection}
                    />
                  </Form.Group>
                </Form>
              </Segment>
            </div>
          </Transition>

          <Button
            primary
            onClick={handleCalculateSAP}
            fluid
            size='large'
            style={{ marginTop: '1em' }}
            disabled={!canCalculate}
            type='submit' // Good practice for the main action button
          >
            <Icon name='calculator' /> Calculate SAP Status
          </Button>
        </>
      ) : (
        <>
          <Transition visible={showResultsAnimation} animation='fade up' duration={500} unmountOnHide>
            <Segment raised style={{ marginTop: '1em', textAlign: 'center' }}>
              <Header as='h2' style={{ marginBottom: '1em' }}>Calculation Results</Header>
              <Grid columns={2} divided stackable>
                <Grid.Row>
                  <Grid.Column>
                    <Header as='h3'>Federal Aid Result</Header>
                    {federalResults.length > 0 ? (
                      federalResults.map((res, index) => <ResultItem key={`fed-${index}`} {...res} />)
                    ) : (
                      <Message info content='No federal results to display.' />
                    )}
                  </Grid.Column>
                  <Grid.Column>
                    <Header as='h3'>NYS TAP Result</Header>
                    {tapResults.length > 0 ? (
                      tapResults.map((res, index) => <ResultItem key={`tap-${index}`} {...res} />)
                    ) : (
                      <Message info content='TAP not evaluated or no specific TAP results for the inputs provided.' />
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Transition>

          <Button
            basic // Makes it look less like a standard button
            onClick={handleReset}
            fluid
            size='large'
            style={{
              marginTop: '2em',
              color: '#4183c4', // Standard link blue
              fontWeight: 'normal',
              padding: '0.5em 0',
              textAlign: 'center',
              // textDecoration: 'underline', // Optional: if you want it always underlined
            }}
            onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
            onFocus={e => e.currentTarget.style.textDecoration = 'underline'}
            onBlur={e => e.currentTarget.style.textDecoration = 'none'}
            role='link' // ARIA role for link behavior
          >
            <Icon name='undo' /> Calculate New / Reset Form
          </Button>
        </>
      )}
    </Container>
  );
};

export default SAPCalculator;
